import { signal } from '@angular/core';
import { arrow, autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { type PopoverVariation, type PopoverVariationName } from './popover-variations';

/** A {@link PopoverVariation} with its per-instance overrides already resolved. */
export interface PopoverInstanceConfig extends PopoverVariation {
  /** Variation name — also used as the `.rcp-popover-variation-<name>` class. */
  variationName: PopoverVariationName;
  /** Any CSS length, e.g. `400px` or `unset`. */
  maxWidth?: string;
  useHostWidth?: boolean;
  onVisibleChange?: (visible: boolean) => void;
}

/** Time to keep the popover mounted while the leave-animation plays. */
const HIDE_ANIMATION_MS = 250;
/** Delay before hiding on host-leave, so the cursor can bridge into an interactive popover. */
const HOVER_BRIDGE_LEAVE_MS = 80;
/** Min gap (px) the popover keeps from the viewport edge — floating-ui `shift` padding. */
const VIEWPORT_PADDING = 8;
/** Min inset (px) of the arrow from the popover corners — floating-ui `arrow` padding. */
const ARROW_PADDING = 4;
/** Floor for the height the `size` middleware reports, so the box stays legible. */
const MIN_AVAILABLE_HEIGHT_PX = 120;

/**
 * Framework-agnostic popover engine on @floating-ui/dom. Renders the DOM
 * structure `[data-rcp-popover-root]` > `.rcp-popover` >
 * `.rcp-popover-content`/`.rcp-popover-arrow` that the tooltip theme SCSS targets.
 */
export class PopoverInstance {
  private root?: HTMLDivElement;
  private box?: HTMLDivElement;
  private contentEl?: HTMLDivElement;
  private arrowEl?: HTMLDivElement;

  private cleanupAutoUpdate?: () => void;
  private hideTimer?: ReturnType<typeof setTimeout>;
  private leaveTimer?: ReturnType<typeof setTimeout>;

  // Reactive state.
  private readonly enabled = signal(true);
  private readonly mounted = signal(false);
  private readonly isVisible = signal(false);
  private readonly cursorX = signal<number | undefined>(undefined);

  constructor(
    private readonly host: HTMLElement,
    private config: PopoverInstanceConfig,
    /** Provides the content to mount: a plain string or live DOM nodes (e.g. an embedded view). */
    private readonly contentProvider: () => string | Node[],
  ) {
    this.setupTriggers();
  }

  get visible(): boolean {
    return this.isVisible();
  }

  show(): void {
    if (!this.enabled() || this.isVisible()) {
      return;
    }
    clearTimeout(this.hideTimer);
    clearTimeout(this.leaveTimer);
    this.mount();
    this.isVisible.set(true);
    this.startAutoUpdate();
    requestAnimationFrame(() => {
      if (this.box && this.isVisible()) {
        this.box.dataset.state = 'visible';
      }
    });
    this.addDismissListeners();
    this.config.onVisibleChange?.(true);
  }

  hide(): void {
    if (!this.isVisible()) {
      return;
    }
    this.isVisible.set(false);
    if (this.box) {
      this.box.dataset.state = 'hidden';
    }
    this.removeDismissListeners();
    this.config.onVisibleChange?.(false);
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      if (!this.isVisible()) {
        this.unmount();
      }
    }, HIDE_ANIMATION_MS);
  }

  toggle(): void {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  enable(): void {
    this.enabled.set(true);
  }

  disable(): void {
    this.enabled.set(false);
    this.hide();
  }

  destroy(): void {
    this.removeTriggers();
    this.removeDismissListeners();
    clearTimeout(this.hideTimer);
    clearTimeout(this.leaveTimer);
    this.unmount();
  }

  // --- DOM -----------------------------------------------------------------

  private mount(): void {
    if (this.mounted()) {
      return;
    }
    const { variationName } = this.config;

    this.root = document.createElement('div');
    this.root.setAttribute('data-rcp-popover-root', '');
    this.root.style.position = 'absolute';
    this.root.style.top = '0';
    this.root.style.left = '0';
    // z-index is driven by CSS (--rcp-popover-z-index, default --z-index-tooltip).
    // The variation class lives on the root so the *OverModal variants can raise it.
    this.root.classList.add(`rcp-popover-variation-${variationName}`);
    // The shift-away animation slides in by the same distance as the positioning
    // gap, so feed the variation's offset distance to the CSS var (single source).
    this.root.style.setProperty('--rcp-popover-animation-shift', `${this.config.offset[1]}px`);

    this.box = document.createElement('div');
    this.box.className = 'rcp-popover';
    this.box.classList.add(`rcp-popover-variation-${variationName}`);
    this.box.dataset.theme = this.config.theme;
    if (this.config.animation) {
      this.box.dataset.animation = this.config.animation;
    }
    if (this.config.interactive) {
      this.box.dataset.interactive = '';
    }
    this.box.dataset.state = 'hidden';
    this.box.dataset.placement = this.config.placement;
    if (this.config.maxWidth) {
      this.box.style.maxWidth = this.config.maxWidth;
    }

    this.contentEl = document.createElement('div');
    this.contentEl.className = 'rcp-popover-content';
    this.box.appendChild(this.contentEl);

    if (this.config.arrow) {
      this.arrowEl = document.createElement('div');
      this.arrowEl.className = 'rcp-popover-arrow';
      this.box.appendChild(this.arrowEl);
    }

    this.renderContent();
    this.root.appendChild(this.box);
    document.body.appendChild(this.root);
    this.mounted.set(true);
  }

  private renderContent(): void {
    if (!this.contentEl) {
      return;
    }
    const content = this.contentProvider();
    this.contentEl.replaceChildren();
    if (typeof content === 'string') {
      this.contentEl.textContent = content;
    } else {
      this.contentEl.append(...content);
    }
  }

  private unmount(): void {
    this.cleanupAutoUpdate?.();
    this.cleanupAutoUpdate = undefined;
    this.root?.remove();
    this.root = this.box = this.contentEl = this.arrowEl = undefined;
    this.mounted.set(false);
  }

  // --- positioning ---------------------------------------------------------

  private startAutoUpdate(): void {
    if (!this.root) {
      return;
    }
    const reference = this.referenceElement();
    this.cleanupAutoUpdate = autoUpdate(reference, this.root, () => this.reposition());
  }

  private reposition(): void {
    if (!this.root || !this.box) {
      return;
    }
    const reference = this.referenceElement();

    if (this.config.useHostWidth) {
      this.box.style.width = `${this.host.offsetWidth}px`;
    }

    const middleware = [
      offset({ mainAxis: this.config.offset[1], crossAxis: this.config.offset[0] }),
      flip(),
      shift({ padding: VIEWPORT_PADDING }),
      // `flip`/`shift` can only move the box; when neither side has vertical
      // room it would run off-screen, so report the space that's left and let
      // the SCSS cap the box (its content scrolls).
      size({
        padding: VIEWPORT_PADDING,
        apply: ({ availableHeight }) => {
          this.root?.style.setProperty(
            '--rcp-popover-available-height',
            `${Math.max(availableHeight, MIN_AVAILABLE_HEIGHT_PX)}px`,
          );
        },
      }),
    ];
    if (this.arrowEl) {
      middleware.push(arrow({ element: this.arrowEl, padding: ARROW_PADDING }));
    }

    computePosition(reference, this.root, {
      placement: this.config.placement,
      strategy: 'absolute',
      middleware,
    }).then(({ x, y, placement, middlewareData }) => {
      if (!this.root || !this.box) {
        return;
      }
      Object.assign(this.root.style, { left: `${x}px`, top: `${y}px` });
      this.box.dataset.placement = placement;

      const arrowData = middlewareData.arrow;
      if (this.arrowEl && arrowData) {
        this.arrowEl.style.left = arrowData.x != null ? `${arrowData.x}px` : '';
        this.arrowEl.style.top = arrowData.y != null ? `${arrowData.y}px` : '';
      }
    });
  }

  /**
   * For `followCursor: 'horizontal'` the reference is a virtual element that keeps
   * the host's vertical rect but tracks the cursor's X — used by the skill
   * development graph so a wide host doesn't push the tooltip off-screen.
   */
  private referenceElement() {
    const cursorX = this.cursorX();
    if (this.config.followCursor !== 'horizontal' || cursorX == null) {
      return this.host;
    }
    const host = this.host;
    return {
      getBoundingClientRect() {
        const rect = host.getBoundingClientRect();
        return {
          width: 0,
          height: rect.height,
          x: cursorX,
          y: rect.y,
          top: rect.top,
          bottom: rect.bottom,
          left: cursorX,
          right: cursorX,
        } as DOMRect;
      },
    };
  }

  // --- triggers ------------------------------------------------------------

  private readonly onHostEnter = () => this.show();
  private readonly onHostLeave = () => this.scheduleLeave();
  private readonly onHostClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.toggle();
  };
  private readonly onHostMove = (event: MouseEvent) => {
    this.cursorX.set(event.clientX);
    if (this.config.followCursor === 'horizontal' && this.isVisible()) {
      this.reposition();
    }
  };

  private setupTriggers(): void {
    const trigger = this.config.trigger;
    if (trigger === 'manual') {
      return;
    }
    if (trigger === 'click') {
      this.host.addEventListener('click', this.onHostClick);
      return;
    }
    // hover
    this.host.addEventListener('mouseenter', this.onHostEnter);
    this.host.addEventListener('mouseleave', this.onHostLeave);
    if (this.config.followCursor === 'horizontal') {
      this.host.addEventListener('mousemove', this.onHostMove);
    }
  }

  private removeTriggers(): void {
    this.host.removeEventListener('click', this.onHostClick);
    this.host.removeEventListener('mouseenter', this.onHostEnter);
    this.host.removeEventListener('mouseleave', this.onHostLeave);
    this.host.removeEventListener('mousemove', this.onHostMove);
  }

  /** Delayed hide on host-leave so the cursor can bridge into an interactive popover. */
  private scheduleLeave(): void {
    if (!this.config.interactive) {
      this.hide();
      return;
    }
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => this.hide(), HOVER_BRIDGE_LEAVE_MS);
  }

  // --- dismiss (outside click / escape / interactive bridge) ---------------

  private readonly onDocumentPointerDown = (event: Event) => {
    const target = event.target as Node;
    if (this.host.contains(target) || this.root?.contains(target)) {
      return;
    }
    this.hide();
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.hide();
    }
  };

  private readonly onBoxEnter = () => clearTimeout(this.leaveTimer);
  private readonly onBoxLeave = () => this.scheduleLeave();

  private addDismissListeners(): void {
    const trigger = this.config.trigger;
    if (trigger === 'click' || trigger === 'manual') {
      // defer so the opening click doesn't immediately close it
      setTimeout(() => {
        if (this.isVisible()) {
          document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
        }
      });
      document.addEventListener('keydown', this.onDocumentKeydown);
    }
    // Hover-bridge: only hover-triggered interactive tooltips close on mouse-leave.
    // Click/manual popovers stay open until an outside click or Escape.
    if (trigger === 'mouseenter' && this.config.interactive && this.box) {
      this.box.addEventListener('mouseenter', this.onBoxEnter);
      this.box.addEventListener('mouseleave', this.onBoxLeave);
    }
  }

  private removeDismissListeners(): void {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.box?.removeEventListener('mouseenter', this.onBoxEnter);
    this.box?.removeEventListener('mouseleave', this.onBoxLeave);
  }
}
